import React, { useState } from 'react';
import {
    Button,
    FormControl,
    FormLabel,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Text,
    VStack,
} from '@chakra-ui/react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import type { IntegrationsLocation } from '../../../hooks/useGetIntegrations';
import PartBayButton from '../../PartBayButton';
import { CONNECTIONS, type ConnectionId } from './catalog';

export interface AddCredentialPayload {
    username: string;
    password: string;
    locationId?: string;
}

export interface AddCredentialModalProps {
    /** Null closes the modal; the id also picks the copy and the location field. */
    connectionId: ConnectionId | null;
    onClose: () => void;
    locations: IntegrationsLocation[];
    /** Locations that already have a credential, hidden from the picker. */
    usedLocationIds: Set<string>;
    onSave: (payload: AddCredentialPayload) => Promise<void>;
    isSaving: boolean;
}


const AddCredentialModal = ({
    connectionId,
    onClose,
    locations,
    usedLocationIds,
    onSave,
    isSaving,
}: AddCredentialModalProps) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [locationId, setLocationId] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const isOpen = connectionId !== null;
    const showLocation = connectionId !== null && CONNECTIONS[connectionId].isPerLocation;
    const availableLocations = locations.filter((location) => !usedLocationIds.has(location._id));

    const reset = () => {
        setUsername('');
        setPassword('');
        setLocationId('');
        setShowPassword(false);
    };

    const handleClose = () => {
        if (isSaving) return;
        reset();
        onClose();
    };

    const isValid =
        username.trim().length > 0 &&
        password.trim().length > 0 &&
        (!showLocation || locationId.length > 0);

    const handleSubmit = async () => {
        if (!isValid) return;
        await onSave({
            username: username.trim(),
            password: password.trim(),
            ...(showLocation ? { locationId } : {}),
        });
        reset();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
            <ModalOverlay />
            <ModalContent borderRadius="xl">
                <ModalHeader fontSize="lg" fontWeight="bold">
                    Add {connectionId ? CONNECTIONS[connectionId].label : ''} Credential
                </ModalHeader>
                <ModalCloseButton isDisabled={isSaving} />
                <ModalBody pb={2}>
                    <VStack spacing={4} align="stretch">
                        <Text fontSize="xs" color="gray.500">
                            Encryopted and stored securely in SSM.
                        </Text>
                        {showLocation && (
                            <FormControl isRequired>
                                <FormLabel fontSize="sm">Location</FormLabel>
                                <Select
                                    placeholder={
                                        availableLocations.length === 0
                                            ? 'All locations already configured'
                                            : 'Select location'
                                    }
                                    value={locationId}
                                    onChange={(e) => setLocationId(e.target.value)}
                                    isDisabled={availableLocations.length === 0}
                                    size="sm"
                                >
                                    {availableLocations.map((location) => (
                                        <option key={location._id} value={location._id}>
                                            {location.title}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <FormControl isRequired>
                            <FormLabel fontSize="sm">Username</FormLabel>
                            <Input
                                size="sm"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="Username"
                                autoComplete="off"
                            />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel fontSize="sm">Password</FormLabel>
                            <InputGroup size="sm">
                                <Input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Password"
                                    autoComplete="new-password"
                                />
                                <InputRightElement>
                                    <IconButton
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                        icon={
                                            <Icon
                                                as={(showPassword ? FaEyeSlash : FaEye) as React.ElementType}
                                            />
                                        }
                                        size="xs"
                                        variant="ghost"
                                        color="gray.500"
                                        onClick={() => setShowPassword((previous) => !previous)}
                                    />
                                </InputRightElement>
                            </InputGroup>
                        </FormControl>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button
                        variant="outlineGray"
                        size="action"
                        onClick={handleClose}
                        isDisabled={isSaving}
                        mr={2}
                    >
                        Cancel
                    </Button>
                    <PartBayButton
                        text="Save Credential"
                        variant="solid"
                        colorScheme="partbay-blue"
                        size="action"
                        onClick={() => void handleSubmit()}
                        isDisabled={!isValid}
                        isLoading={isSaving}
                    />
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default AddCredentialModal;
